import { useState, useEffect } from "react";
import { supabase } from "../../supabase";
import ConfirmModal from "../ConfirmModal";

const ROLES = ["architect", "contractor", "client"];
const ROLE_LABELS = { architect: "Architecte", contractor: "Entreprise", client: "Client" };

function StatusBadge({ active }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
      padding: "3px 8px", borderRadius: 4,
      background: active ? "#A8C97E18" : "#FC818118",
      border: `1px solid ${active ? "#A8C97E40" : "#FC818140"}`,
      color: active ? "#A8C97E" : "#FC8181",
    }}>
      {active ? "Actif" : "Inactif"}
    </span>
  );
}

function RoleDropdown({ arch, open, onToggle, onSelect }) {
  return (
    <div style={{ position: "relative" }}>
      <button
        className="btn"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #2A2924", fontSize: 11, color: "#E8E2D8", background: "#252420", fontWeight: 500 }}
      >
        Rôle ▾
      </button>
      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, background: "#1A1916", border: "1px solid #2A2924", borderRadius: 8, zIndex: 200, minWidth: 140, boxShadow: "0 8px 24px #00000060", overflow: "hidden" }}
        >
          {ROLES.map(role => (
            <button
              key={role}
              className="btn"
              onClick={() => onSelect(role)}
              style={{ display: "block", width: "100%", padding: "9px 14px", textAlign: "left", fontSize: 13, color: role === arch.role ? "#C9A96E" : "#E8E2D8", background: "none" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#252420"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
            >
              {ROLE_LABELS[role]}{role === arch.role ? " ✓" : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminArchitects() {
  const [architects, setArchitects] = useState([]);
  const [projectCounts, setProjectCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openRoleId, setOpenRoleId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const fetchData = async () => {
    const { data: archs } = await supabase
      .from("profiles")
      .select("id, name, email, created_at, is_active")
      .eq("role", "architect")
      .order("created_at", { ascending: false });

    if (!archs || archs.length === 0) { setLoading(false); return; }

    const { data: projects } = await supabase
      .from("projects")
      .select("owner_id")
      .in("owner_id", archs.map(a => a.id));

    const counts = {};
    (projects || []).forEach(p => { counts[p.owner_id] = (counts[p.owner_id] || 0) + 1; });

    setArchitects(archs);
    setProjectCounts(counts);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!openRoleId) return;
    const handler = () => setOpenRoleId(null);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [openRoleId]);

  const toggleActive = async (arch) => {
    const newVal = !(arch.is_active !== false);
    const { error } = await supabase.from("profiles").update({ is_active: newVal }).eq("id", arch.id);
    if (error) { showToast("❌ " + error.message); return; }
    setArchitects(prev => prev.map(a => a.id === arch.id ? { ...a, is_active: newVal } : a));
  };

  const changeRole = async (arch, newRole) => {
    setOpenRoleId(null);
    if (newRole === arch.role) return;
    const { error } = await supabase.from("profiles").update({ role: newRole }).eq("id", arch.id);
    if (error) { showToast("❌ " + error.message); return; }
    setArchitects(prev => prev.filter(a => a.id !== arch.id));
    showToast(`✓ Rôle → ${ROLE_LABELS[newRole]}`);
  };

  const confirmDelete = async () => {
    const { error } = await supabase.from("profiles").delete().eq("id", deleteTarget.id);
    if (error) { showToast("❌ " + error.message); setDeleteTarget(null); return; }
    setArchitects(prev => prev.filter(a => a.id !== deleteTarget.id));
    setDeleteTarget(null);
    showToast("✓ Compte supprimé");
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, fontWeight: 400 }}>Architectes</h1>
        <p style={{ fontSize: 13, color: "#7A7468", marginTop: 4 }}>
          {loading ? "Chargement…" : `${architects.length} compte${architects.length !== 1 ? "s" : ""}`}
        </p>
      </div>

      <div style={{ background: "#1A1916", border: "1px solid #2A2924", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #2A2924" }}>
              {["Nom", "Email", "Inscription", "Projets", "Statut", "Actions"].map(h => (
                <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 10, color: "#7A7468", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ padding: 48, textAlign: "center" }}>
                  <div className="spinner" style={{ margin: "0 auto" }} />
                </td>
              </tr>
            ) : architects.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 48, textAlign: "center", color: "#7A7468", fontSize: 13 }}>
                  Aucun architecte enregistré
                </td>
              </tr>
            ) : architects.map((arch, i) => {
              const isActive = arch.is_active !== false;
              return (
                <tr key={arch.id} style={{ borderBottom: i < architects.length - 1 ? "1px solid #1F1E1A" : "none" }}>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#252420", border: "1px solid #2A2924", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#7EB8C9", flexShrink: 0 }}>▲</div>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{arch.name || "—"}</span>
                    </div>
                  </td>
                  <td style={{ padding: "13px 16px", fontSize: 13, color: "#7A7468" }}>{arch.email || "—"}</td>
                  <td style={{ padding: "13px 16px", fontSize: 12, color: "#7A7468", whiteSpace: "nowrap" }}>{fmtDate(arch.created_at)}</td>
                  <td style={{ padding: "13px 16px" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: projectCounts[arch.id] > 0 ? "#C9A96E" : "#7A7468" }}>
                      {projectCounts[arch.id] || 0}
                    </span>
                  </td>
                  <td style={{ padding: "13px 16px" }}>
                    <StatusBadge active={isActive} />
                  </td>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <button
                        className="btn"
                        onClick={() => toggleActive(arch)}
                        style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 500, whiteSpace: "nowrap", border: `1px solid ${isActive ? "#FC818140" : "#A8C97E40"}`, color: isActive ? "#FC8181" : "#A8C97E", background: isActive ? "#FC818112" : "#A8C97E12" }}
                      >
                        {isActive ? "Désactiver" : "Activer"}
                      </button>

                      <RoleDropdown
                        arch={{ ...arch, role: "architect" }}
                        open={openRoleId === arch.id}
                        onToggle={() => setOpenRoleId(openRoleId === arch.id ? null : arch.id)}
                        onSelect={(role) => changeRole(arch, role)}
                      />

                      <button
                        className="btn"
                        onClick={() => setDeleteTarget(arch)}
                        style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #FC818140", fontSize: 11, color: "#FC8181", background: "#FC818112", fontWeight: 500 }}
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Supprimer ce compte ?"
          message={`Le compte de ${deleteTarget.name || deleteTarget.email || "cet architecte"} sera définitivement supprimé.`}
          confirmLabel="Supprimer"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
